import { useEffect, useRef, useState } from 'react';
import { Video } from '../../../types/Video';
import { Play } from 'react-feather';
import { incrementVideoView } from '../../../services/videos';
import posthog from 'posthog-js';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { TriangleRight } from '../../../assets/svgs/TriangleRight';

const VideoPlayer: React.FC<{
  title: string;
  cover: string;
  video: Video;
  showSpeedOptions?: boolean;
}> = ({ title, cover, video, showSpeedOptions = true }) => {
  const [showCover, setShowCover] = useState(true);
  const [playbackRate, setPlaybackRate] = useState<number>(1);
  const [hasIncrementedView, setHasIncrementedView] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);

  // When the video is changed, show the cover again and reset the video
  useEffect(() => {
    setShowCover(true);
    setHasIncrementedView(false);
    if (videoRef.current) {
      videoRef.current.load(); // This forces the video to reload with new sources
      videoRef.current.pause();
    }
  }, [video]);

  const changeSpeed = (speed: number) => {
    const video = videoRef.current;
    if (video) {
      video.playbackRate = speed;
      setPlaybackRate(speed);
    }
  };

  const handlePlay = async () => {
    if (!hasIncrementedView) {
      try {
        await incrementVideoView(video.id);
        setHasIncrementedView(true);
        posthog?.capture('video_viewed', {
          video_id: video.id,
          video_title: video.title,
        });
      } catch (error) {
        console.error('Failed to increment video view:', error);
      }
    }
  };

  return (
    <div className="video-player">
      <div className="video-player_video">
        {showCover && (
          <div
            className="video-player_video_cover"
            onClick={() => {
              setShowCover(false);
              if (videoRef.current) {
                videoRef.current.play();
              }
            }}
          >
            <div className="title">{title}</div>
            <div className="play-button">
              <Play />
            </div>
            <img src={cover} alt={title} />
          </div>
        )}
        <video
          ref={videoRef}
          width="100%"
          height="auto"
          style={{ maxWidth: '100%', borderRadius: '.5rem' }}
          controls
          poster={video.thumbnailUrl || undefined}
          onPlay={handlePlay}
        >
          {video.mp4Url && <source src={video.mp4Url} type="video/mp4" />}
          {video.webmUrl && <source src={video.webmUrl} type="video/webm" />}
          Your browser does not support the video tag.
        </video>
      </div>
      <div className="video-player_info">
        <div className="video-player_info_left">
          {video.title && (
            <h3 className="video-player_info_left_title">{video.title}</h3>
          )}
          {video.description && (
            <div className="video-player_info_left_description">
              <p>{video.description}</p>
            </div>
          )}
          <div className="caption-credit m-t-0-75">
            <div className="credit">
              <a
                href={video.citationUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {video.citationAuthor}
              </a>{' '}
              |{' '}
              <a
                href="https://creativecommons.org/licenses/by-sa/4.0/deed.en"
                target="_blank"
                rel="noopener noreferrer"
              >
                {video.citationLicense}
              </a>
            </div>
          </div>
        </div>
        {showSpeedOptions && (
          <div className="video-player_info_right">
            <Select
              id="playback-speed"
              value={playbackRate}
              onChange={(e) => {
                changeSpeed(e.target.value as number);
              }}
            >
              <MenuItem value={0.25}>
                <TriangleRight />
                .25x
              </MenuItem>
              <MenuItem value={0.5}>
                <TriangleRight />
                .5x
              </MenuItem>
              <MenuItem value={0.75}>
                <TriangleRight />
                .75x
              </MenuItem>
              <MenuItem value={1}>
                <TriangleRight />
                1x
              </MenuItem>
              <MenuItem value={1.5}>
                <TriangleRight />
                1.5x
              </MenuItem>
              <MenuItem value={2}>
                <TriangleRight />
                2x
              </MenuItem>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
